// Hideo - the script editor's outline (src/components/editor/ScriptToc.vue): headings 1–3 of the page, the active one
// following the scrolling, open over the page below 1024 px. Ported from the legacy editor.js (renderToc, queueToc,
// tocGo, tocActive, toggleToc) with the same moments: it is drawn when render() is called (opening, tab, mode, 250 ms
// after a change) and the active heading is measured at that moment and on scrolling. The headings are the page's
// elements. The writing surface (src/features/editor/surface/) calls queue, toggle and reads open.
import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import { edCaret } from '../features/editor/surface/core'
import { tocHeadings, tocText } from '../features/editor/toc'
import { edBodyEl, setEdTop } from '../features/editor/html'

/** A heading of the outline as drawn: its level ('1'…'3') and text */
interface TocItem { level: string; text: string }

export const useTocStore = defineStore('toc', () => {
  /** Drawn at least once (before that the column is empty, like the legacy one) */
  const items = shallowRef<TocItem[]>([])
  const seq = ref(0)
  /** Index of the active heading */
  const active = ref(0)
  /** Open over the page (below 1024 px: .toc-open) */
  const open = ref(false)
  let heads: HTMLElement[] = [], timer: ReturnType<typeof setTimeout> | undefined, raf = 0

  /** Draws the outline from the page's headings */
  function render(): void {
    const body = edBodyEl()
    if (!body) return
    heads = tocHeadings(body)
    const next = heads.map(h => ({ level: h.tagName[1], text: tocText(h) }))
    // The same headings: the buttons stay (seq keys them: a new seq draws them all again, 250 ms after every change)
    if (!seq.value || JSON.stringify(next) !== JSON.stringify(items.value)) { items.value = next; seq.value++ }
    activeNow()
  }
  /** Drawn again 250 ms after the last change */
  function queue(): void { clearTimeout(timer); timer = setTimeout(render, 250) }
  /** The active heading: the last one whose top is above the page's top + 90 px */
  function activeNow(): void {
    if (!heads.length) return
    const lim = (document.getElementById('scriptVisualWrap') as HTMLElement).getBoundingClientRect().top + 90
    let k = 0
    heads.forEach((h, i) => { if (h.getBoundingClientRect().top <= lim) k = i })
    active.value = k
  }
  /** Scrolling: the active heading at the next frame */
  function onScroll(): void { cancelAnimationFrame(raf); raf = requestAnimationFrame(activeNow) }
  /** A heading tapped: scrolls to it and puts the cursor at its end (below 1024 px the outline closes) */
  function go(i: number): void {
    const h = heads[i]
    if (!h || !h.isConnected) return render()
    h.scrollIntoView({ behavior: 'smooth', block: 'start' })
    ;edBodyEl().focus({ preventScroll: true }); edCaret(h, h.childNodes.length)
    if (innerWidth < 1024) toggle(false)
  }
  /** Opens or closes the outline over the page (on = toggles); opening draws it again */
  function toggle(on?: boolean): void {
    on = on ?? !open.value
    open.value = on
    if (on) { setEdTop(); render() }
  }
  return { items, seq, active, open, render, queue, activeNow, onScroll, go, toggle }
})
